import { useCallback } from 'react';

const STATS = [
  { value: '10,000+', label: '剧集资源覆盖' },
  { value: '每日更新', label: '热剧同步追更' },
  { value: '1080P', label: '高清画质保障' },
];

export default function HeroSection() {
  const scrollToAnchor = useCallback((href: string) => {
    const target = document.querySelector(href);
    if (target) {
      const headerHeight = 64;
      const top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=A%20warm%20atmospheric%20cinema%20interior%20with%20soft%20orange%20and%20amber%20lighting%2C%20comfortable%20velvet%20seats%20arranged%20in%20rows%2C%20a%20large%20screen%20glowing%20with%20warm%20golden%20light%2C%20elegant%20art%20deco%20details%20on%20the%20walls%2C%20golden%20curtains%20framing%20the%20screen%2C%20luxurious%20yet%20cozy%20movie%20theater%20ambiance%2C%20rich%20warm%20orange%20and%20amber%20tones%2C%20cinematic%20composition%2C%20photorealistic%20rendering%2C%208K%20quality&width=1600&height=900&seq=hero-bg-juzi-01&orientation=landscape"
          alt="桔子影院 - 沉浸式观影体验"
          className="w-full h-full object-cover object-top"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/65"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          {/* Left: Text */}
          <div className="flex-1 w-full text-center lg:text-left pt-20 md:pt-0">
            {/* Tag */}
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/90 text-background-50 text-xs md:text-sm font-medium tracking-wide whitespace-nowrap mb-5">
              热播追剧第一站
            </span>

            {/* H1 */}
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4">
              桔子影院
              <span className="block text-lg md:text-2xl lg:text-3xl font-medium text-background-50/85 mt-2">
                电视剧全集完整剧集免费在线追剧
              </span>
            </h1>

            {/* Slogan */}
            <p className="text-sm md:text-base text-background-50/75 mb-6">
              国产剧海外剧高清不缓冲，追剧不等待
            </p>

            {/* Intro */}
            <p className="text-sm md:text-base text-background-50/70 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8">
              桔子影院汇聚全网热播电视剧全集完整剧集，覆盖国产剧、海外剧全季资源。每日同步更新最新剧集，支持高清画质流畅播放，让追剧不再等待。无论是最新大结局还是经典老剧全季，都能在这里一站畅享。
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-6 mb-8 max-w-md mx-auto lg:mx-0">
              {STATS.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-lg md:text-2xl font-bold text-accent-400 font-heading whitespace-nowrap">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-background-50/60 mt-1 whitespace-nowrap">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
              <a
                href="#cards"
                onClick={(e) => { e.preventDefault(); scrollToAnchor('#cards'); }}
                className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm whitespace-nowrap transition-all duration-200 cursor-pointer text-center"
              >
                开始追剧
              </a>
              <a
                href="#carousel"
                onClick={(e) => { e.preventDefault(); scrollToAnchor('#carousel'); }}
                className="w-full sm:w-auto px-8 py-3 rounded-full border border-background-50/40 hover:border-background-50 hover:bg-background-50/10 text-background-50 font-medium text-sm whitespace-nowrap transition-all duration-200 cursor-pointer text-center"
              >
                查看推荐
              </a>
            </div>
          </div>

          {/* Right: Visual */}
          <div className="flex-1 w-full max-w-md lg:max-w-lg hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-accent-500/10 rounded-2xl blur-2xl"></div>
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20cinematic%20scene%20with%20soft%20orange%20and%20amber%20lighting%2C%20luxurious%20home%20theater%20setup%20with%20a%20large%20glowing%20screen%20displaying%20a%20dramatic%20scene%2C%20cozy%20atmosphere%20with%20warm%20ambient%20light%2C%20popcorn%20and%20comfortable%20seating%2C%20cinematic%20color%20grading%20with%20rich%20golden%20tones%2C%20photorealistic%20rendering%2C%20warm%20inviting%20aesthetic&width=800&height=600&seq=hero-visual-juzi-02&orientation=landscape"
                alt="桔子影院 - 畅享高清追剧体验"
                className="w-full rounded-2xl shadow-2xl relative z-10 object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}